import { Controller } from "@hotwired/stimulus"

// Dynamic add/remove rows for video URLs (up to data-max-value), plus friendly,
// client-side validation of each URL. Allowed providers embed reliably;
// Meta (Facebook/Instagram) is blocked because their videos show
// "Video unavailable" when embedded off-platform. The model re-validates on
// save as a backstop (see User#video_urls_must_be_valid).
export default class extends Controller {
  static targets = ["container", "template", "row", "addButton", "counter", "error"]
  static values = {
    max: { type: Number, default: 10 },
    metaMessage: { type: String, default: "Facebook and Instagram videos can't be embedded. Use YouTube, Vimeo, TikTok, or Rumble." },
    invalidMessage: { type: String, default: "Enter a YouTube, Vimeo, TikTok, or Rumble (embed) link." }
  }

  // Mirrors User::ALLOWED_VIDEO_PROVIDERS / BLOCKED_VIDEO_PROVIDERS.
  static ALLOWED = /^(https?:\/\/)?(www\.|m\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/|vimeo\.com\/|tiktok\.com\/(@[\w.\-]+\/video\/|embed\/)|rumble\.com\/embed\/)/i
  static META = /(facebook\.com|fb\.watch|instagram\.com)/i

  connect() {
    this.refresh()
    this.onFieldEvent = this.onFieldEvent.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.element.addEventListener("input", this.onFieldEvent)
    this.element.addEventListener("focusout", this.onFieldEvent)
    this.form = this.element.closest("form")
    if (this.form) this.form.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    this.element.removeEventListener("input", this.onFieldEvent)
    this.element.removeEventListener("focusout", this.onFieldEvent)
    if (this.form) this.form.removeEventListener("submit", this.onSubmit)
  }

  add(event) {
    event.preventDefault()
    if (this.rowTargets.length >= this.maxValue) return

    const fragment = this.templateTarget.content.cloneNode(true)
    this.containerTarget.appendChild(fragment)
    this.refresh()

    const newInput = this.containerTarget.querySelector("div:last-child input[type='text']")
    if (newInput) newInput.focus()
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest("[data-video-url-list-target='row']")
    if (!row) return

    if (this.rowTargets.length <= 1) {
      const input = row.querySelector("input[type='text']")
      if (input) { input.value = ""; this.clearField(input) }
    } else {
      row.remove()
    }
    this.refresh()
    this.updateSharedError()
  }

  // --- validation ---------------------------------------------------------

  onFieldEvent(event) {
    const input = event.target
    if (!input.matches("input[type='text']")) return
    this.validateField(input)
    this.updateSharedError()
  }

  onSubmit(event) {
    const invalid = this.videoInputs().filter((input) => !this.validateField(input))
    if (invalid.length > 0) {
      event.preventDefault()
      this.updateSharedError()
      invalid[0].focus()
      invalid[0].scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  // Returns true if valid (or blank). Sets/clears the red border on the input.
  validateField(input) {
    const value = input.value.trim()
    if (value === "") { this.clearField(input); return true }

    const err = this.constructor.META.test(value)
      ? this.metaMessageValue
      : (!this.constructor.ALLOWED.test(value) ? this.invalidMessageValue : null)

    if (err) { this.markField(input, err); return false }
    this.clearField(input)
    return true
  }

  markField(input, message) {
    input.classList.add("border-red-500")
    input.dataset.videoError = message
  }

  clearField(input) {
    input.classList.remove("border-red-500")
    delete input.dataset.videoError
  }

  updateSharedError() {
    if (!this.hasErrorTarget) return
    const firstBad = this.videoInputs().find((i) => i.dataset.videoError)
    if (firstBad) {
      this.errorTarget.textContent = firstBad.dataset.videoError
      this.errorTarget.classList.remove("hidden")
    } else {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }

  videoInputs() {
    return Array.from(this.containerTarget.querySelectorAll("input[type='text']"))
  }

  refresh() {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = String(this.rowTargets.length)
    }
    if (this.hasAddButtonTarget) {
      const atMax = this.rowTargets.length >= this.maxValue
      this.addButtonTarget.disabled = atMax
      this.addButtonTarget.classList.toggle("opacity-50", atMax)
      this.addButtonTarget.classList.toggle("cursor-not-allowed", atMax)
    }
  }
}
